import React, { useEffect, useState } from "react";

const DcaCalculator = ({ profileName }) => {
  const [positions, setPositions] = useState([]);
  const [entryPrice, setEntryPrice] = useState("");
  const [quantity, setQuantity] = useState("");
  const [side, setSide] = useState("long");
  const [lastDeleted, setLastDeleted] = useState(null);
  const [editIndex, setEditIndex] = useState(null);

  const API = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const pass = localStorage.getItem(`pass_${profileName}`);
    if (!pass) return;

    fetch(`${API}/api/load?name=${profileName}&password=${pass}`)
      .then((res) => {
        if (!res.ok) throw new Error("Không tải được vị thế.");
        return res.json();
      })
      .then((data) => {
        if (Array.isArray(data)) {
          setPositions(data);
        } else {
          setPositions([]);
        }
      })
      .catch((err) => {
        console.error(err);
        setPositions([]);
      });
  }, [profileName]);

  const savePositions = (updated) => {
    if (!Array.isArray(updated)) return;
    setPositions(updated);
    fetch(`${API}/api/save?name=${profileName}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ positions: updated }),
    });
  };

  const handleAdd = () => {
    if (!entryPrice || !quantity || parseFloat(quantity) <= 0) return;
    const newEntry = {
      price: parseFloat(entryPrice),
      qty: parseFloat(quantity),
      side,
    };

    let updated;
    if (editIndex !== null) {
      updated = [...positions];
      updated[editIndex] = newEntry;
      setEditIndex(null);
    } else {
      updated = [...positions, newEntry];
    }

    savePositions(updated);
    setEntryPrice("");
    setQuantity("");
  };

  const handleDelete = (index) => {
    const deleted = positions[index];
    const updated = positions.filter((_, i) => i !== index);
    setLastDeleted({ deleted, index });
    savePositions(updated);
  };

  const undoDelete = () => {
    if (!lastDeleted) return;
    const updated = [...positions];
    updated.splice(lastDeleted.index, 0, lastDeleted.deleted);
    savePositions(updated);
    setLastDeleted(null);
  };

  const handleEdit = (index) => {
    const p = positions[index];
    setEntryPrice(p.price);
    setQuantity(p.qty);
    setSide(p.side);
    setEditIndex(index);
  };

  const calcDCA = (type) => {
    const filtered = positions.filter((p) => p.side === type);
    const totalQty = filtered.reduce((acc, cur) => acc + cur.qty, 0);
    const totalCost = filtered.reduce(
      (acc, cur) => acc + cur.price * cur.qty,
      0
    );
    return totalQty ? (totalCost / totalQty).toFixed(2) : "-";
  };

  const totalQty = (type) =>
    positions
      .filter((p) => p.side === type)
      .reduce((sum, p) => sum + p.qty, 0)
      .toFixed(2);

  const getColorClass = (side) => {
    return side === "long"
      ? "border-green-400 bg-green-50"
      : "border-red-400 bg-red-50";
  };

  return (
    <div className="p-4 space-y-6 max-w-2xl mx-auto">
      <h1 className="text-xl font-semibold text-center">
        DCA Calculator - {profileName}
      </h1>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <input
          type="number"
          placeholder="Giá"
          value={entryPrice}
          onChange={(e) => setEntryPrice(e.target.value)}
          className="border px-3 py-2 rounded col-span-2"
        />
        <input
          type="number"
          placeholder="Số lượng"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          className="border px-3 py-2 rounded"
        />
        <div className="flex items-center space-x-2">
          <label className="text-green-700">
            <input
              type="radio"
              value="long"
              checked={side === "long"}
              onChange={() => setSide("long")}
              className="accent-green-600"
            />{" "}
            Long
          </label>
          <label className="text-red-700">
            <input
              type="radio"
              value="short"
              checked={side === "short"}
              onChange={() => setSide("short")}
              className="accent-red-600"
            />{" "}
            Short
          </label>
        </div>
      </div>

      <button
        onClick={handleAdd}
        className="bg-blue-600 text-white py-2 px-4 rounded w-full sm:w-auto"
      >
        {editIndex !== null ? "💾 Cập nhật" : "➕ Thêm vị thế"}
      </button>

      {lastDeleted && (
        <div className="text-sm text-yellow-600">
          Đã xoá một vị thế.{" "}
          <button onClick={undoDelete} className="underline text-blue-600">
            Hoàn tác
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {["long", "short"].map((type) => (
          <div
            key={type}
            className={`border rounded p-3 shadow ${getColorClass(type)}`}
          >
            <h2 className="font-semibold mb-2">{type.toUpperCase()}</h2>
            <ul className="space-y-1 text-sm">
              {positions
                .filter((p) => p.side === type)
                .map((p) => {
                  const realIndex = positions.findIndex((pos) => pos === p);
                  return (
                    <li
                      key={realIndex}
                      className="flex justify-between items-center border-b py-1"
                    >
                      <span className="text-base font-medium">
                        🪙 {p.qty} @ {p.price}
                      </span>
                      <div className="flex space-x-2 text-xs">
                        <button
                          onClick={() => handleEdit(realIndex)}
                          className="text-blue-600 hover:underline"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(realIndex)}
                          className="text-red-600 hover:underline"
                        >
                          ❌
                        </button>
                      </div>
                    </li>
                  );
                })}
            </ul>
            <div className="mt-2 text-sm">
              📌 DCA: <strong>{calcDCA(type)}</strong>
            </div>
            <div className="mt-1 text-xs text-gray-600">
              🧮 Tổng: <strong>{totalQty(type)} 🪙</strong>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DcaCalculator;
